import { CloseIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { appointmentRepository } from "api/appointmentRepository";
import { assetRepository } from "api/assetRepository";
import { organizationRepository } from "api/organizationRepository";
import Card from "components/card/Card";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function CreateAppointment() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const navigate = useNavigate();

  const [assets, setAssets] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const centerAlertTimerRef = useRef<number | null>(null);

  const showCenteredAlert = (
    message: string,
    status: "success" | "error" | "info" | "warning" = "success"
  ) => {
    if (centerAlertTimerRef.current) {
      window.clearTimeout(centerAlertTimerRef.current);
    }

    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);

    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
    }, 1500);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({
    assetId: "",
    assetName: "",
    assetEquipmentId: "",
    appointmentDate: "",
    status: "Pending",
  });

  //  Fetch assets, organizations, vendors
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assetData, orgData] = await Promise.all([
          assetRepository.getAssets(),
          organizationRepository.getOrganizations(),
        ]);
        setAssets(assetData);
        setOrganizations(orgData);
      } catch (error) {
      }
    };
    fetchData();
  }, []);

  // Handle input/select changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // For asset and vendor, store both ID and name
    if (name === "assetId") {
      const selectedAsset = assets.find((a) => a.assetId === value);
      setFormValues((prev) => ({
        ...prev,
        assetId: value,
        assetName: selectedAsset?.name ?? "",
        assetEquipmentId: selectedAsset?.equipmentId ?? "",
      }));
    } else if (name === "vendorId") {
      const selectedVendor = vendors.find((v) => v._id === value || v.vendorId === value);
      setFormValues((prev) => ({
        ...prev,
        vendorId: value,
        vendorName: selectedVendor?.name || selectedVendor?.vendorName || "",
      }));
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  //  Reset form
  const handleReset = () => {
    setFormValues({
      assetId: "",
      assetName: "",
      assetEquipmentId: formValues.assetEquipmentId,
      appointmentDate: "",
      status: "Pending",
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { assetId, appointmentDate } = formValues;

    if (!assetId || !appointmentDate) {
      showCenteredAlert("Please fill all required fields.", "error");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        assetId: formValues.assetId,
        assetName: formValues.assetName,
        scheduledDate: new Date(formValues.appointmentDate),
        status: "Pending",
      };

      const response = await appointmentRepository.postAppointment(payload);
      handleReset();
      showCenteredAlert("Appointment created successfully.", "success");
      setTimeout(() => {
        navigate("/admin/AppointmentList");
      }, 1500);
    } catch (error) {
      showCenteredAlert("Failed to create appointment.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Flex
      direction="column"
      h="100vh"
      align="center"
      justify="flex-start"
      position="relative"
    >
      {/* Background */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
      />

      {/* Card */}
      <Card
        w={{ sm: "330px", md: "700px", lg: "850px" }}
        p="30px"
        mt={{ md: '120px' }}
        mx="auto"
        overflowY="auto"
        zIndex={1}
        borderRadius="16px"
      >
        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
          Create Appointment
        </Text>
        <IconButton
          icon={<CloseIcon />}
          aria-label="Close"
          position="absolute"
          top="10px"
          right="10px"
          size="sm"
          onClick={() => navigate('/admin/AppointmentList')}
        />

        <Flex direction="column" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            {/* Left Column */}
            <Stack gap="20px">
              {/* Asset */}
              <Flex direction="column">
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Asset
                </Text>
                <Select
                  name="assetId"
                  placeholder="Select Asset"
                  value={formValues.assetId}
                  onChange={handleChange}
                >
                  {assets.map((item) => (
                    <option key={item.assetId} value={item.assetId}>
                      {item.name} {item.assetEquipmentId ? `(${item.assetEquipmentId})` : ""}
                    </option>
                  ))}
                </Select>
              </Flex>

              {/* Appointment Date */}
              <Flex direction="column">
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Appointment Date
                </Text>
                <Input
                  type="date"
                  name="appointmentDate"
                  value={formValues.appointmentDate}
                  onChange={handleChange}
                />
              </Flex>

            </Stack>

            {/* Right Column */}
            <Stack gap="20px">


              {/* Status (Read-only) */}
              <Flex direction="column">
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Status
                </Text>
                <Select
                  name="status"
                  value={formValues.status}
                  isDisabled
                >
                  <option value="Pending">Pending</option>
                </Select>
              </Flex>
            </Stack>
          </SimpleGrid>

          {/* Buttons */}
          <Flex justify="center" gap="20px" mt="30px">
            <Button
              variant="darkBrand"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: "128px", md: "148px" }}
              h="46px"
              onClick={handleReset}
              isDisabled={loading}
            >
              Reset
            </Button>

            <Button
              variant="darkBrand"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: "128px", md: "148px" }}
              h="46px"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              isDisabled={isSubmitting}
            >
              Submit
            </Button>
          </Flex>


          {centerAlertVisible && (
            <Center
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              zIndex={60}
              pointerEvents="none"
            >
              <Alert
                status={centerAlertStatus}
                variant="solid"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                width="600px"
                borderRadius="14px"
                boxShadow="xl"
                py={10}
                px={16}
                pointerEvents="auto"
              >
                <AlertIcon boxSize="40px" mr={0} />

                <Text fontSize="lg" fontWeight="700" mt={1}>
                  {centerAlertMessage}
                </Text>
              </Alert>
            </Center>
          )}

        </Flex>
      </Card>
    </Flex>
  );
}
